import { type ProjectFile } from "@overmap-ai/common"
import { selectProjectFileById } from "@overmap-ai/core"
import { LngLatBounds, type LngLatLike } from "mapbox-gl"
import { memo, useMemo } from "react"

import { useFetchFileFromFileModel } from "../../hooks/utils"
import { sdk } from "../../sdk"
import { useAppSelector } from "../../store"
import { selectIsProjectFileVisible } from "../../store/selectors"
import { GeoJsonLayer } from "./GeoJsonLayer"
import { ImageLayer } from "./ImageLayer"

interface ProjectFileLayerProps {
	projectFileId: ProjectFile["uuid"]
}

export const ProjectFileLayer = memo<ProjectFileLayerProps>((props) => {
	const { projectFileId } = props

	const projectFile = useAppSelector(selectProjectFileById(projectFileId))

	if (!projectFile) {
		throw new Error(`Expected Project File with uuid ${projectFileId} to exist, got ${projectFile}`)
	}

	if (!projectFile.bounds) {
		throw new Error(`Expected Project File to have bounds, got ${projectFile.bounds}`)
	}

	const [file] = useFetchFileFromFileModel(projectFile, sdk.projectFiles)

	const isVisible = useAppSelector(selectIsProjectFileVisible(projectFileId))

	const transformedBounds = useMemo(() => {
		return new LngLatBounds(projectFile.bounds!.coordinates as [LngLatLike, LngLatLike])
	}, [projectFile.bounds.coordinates])

	if (!file || !isVisible) return null

	return projectFile.file_extension === "geojson" ? (
		<GeoJsonLayer file={file} />
	) : (
		<ImageLayer file={file} bounds={transformedBounds} />
	)
})
ProjectFileLayer.displayName = "ProjectFileLayer"
